// segmentsList.js — saved segments library
//
// Uses the global ActiveHelper/MainPage pattern so the table, search, export,
// pagination, action buttons, and delete confirmation are consistent with the
// rest of the admin (vouchers, marketingVouchers, admins, etc.).
//
// Note: segment edit is a full-page form (rule builder + reach panel) — too
// complex for the Helper modal — so customEditFunction navigates to the
// dedicated /segments/:id/edit route instead of opening a modal.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ActiveHelper from '../../../../components/common/activeHelper';
import { firestore } from '../../../../helpers/Firebase';
import { AuditLogModal, useAuditLogModal, actorFromUser, writeAuditEntry } from '../shared/auditLog.js';

const countLeafRules = (group) => {
  if (!group || !Array.isArray(group.rules)) return 0;
  return group.rules.reduce((acc, r) => acc + (r.rules ? countLeafRules(r) : 1), 0);
};

const SegmentsList = ({ history }) => {
  const { user } = useSelector(state => state.authUser);
  const search = useSelector(state => state.settings.search) || '';

  const [segments, setSegments] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // Subscribe to marketing_segments
  useEffect(() => {
    const unsub = firestore()
      .collection('marketing_segments')
      .orderBy('updatedAt', 'desc')
      .onSnapshot(
        (snap) => setSegments(snap.docs.map(d => ({ id: d.id, key: d.id, ...d.data() }))),
        (err) => console.error('segments listener error', err)
      );
    return () => unsub();
  }, []);

  // Apply search filter (matches existing voucher screen pattern)
  useEffect(() => {
    if (!search || !search.trim()) {
      setFiltered(segments);
      return;
    }
    const q = search.toLowerCase().trim();
    setFiltered(segments.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.description || '').toLowerCase().includes(q)
    ));
  }, [segments, search]);

  // Decorate raw docs with computed columns expected by tableColumns
  const decorated = useMemo(() => filtered.map(s => ({
    ...s,
    _ruleCount: countLeafRules(s.ruleTree),
    _owner: s.updatedByName || s.createdByName || '—',
    _updatedAt: s.updatedAt || s.createdAt,
  })), [filtered]);

  const tableColumns = useMemo(() => [
    { Header: 'Name',         accessor: 'name',         type: 'label' },
    { Header: 'Description',  accessor: 'description',  type: 'label' },
    { Header: 'Rules',        accessor: '_ruleCount',   type: 'label' },
    {
      Header: 'Last updated',
      accessor: '_updatedAt',
      type: 'label',
      formatValue: (v) => v ? moment(v).fromNow() : '—',
    },
    { Header: 'Owner',        accessor: '_owner',       type: 'label' },
  ], []);

  // Custom edit → full-page editor (modal too small for rule builder + reach panel)
  const handleEdit = useCallback((seg) => {
    if (!seg) return;
    history.push(`/app/marketing/segments/${seg.id || seg.key}/edit`);
  }, [history]);

  const handleCreate = useCallback(() => {
    history.push('/app/marketing/segments/new');
  }, [history]);

  const handleDelete = useCallback(async (seg) => {
    if (!seg) return;
    const id = seg.id || seg.key;
    try {
      writeAuditEntry('marketing_segments', id, {
        ...actorFromUser(user),
        action: 'deleted',
        notes: `Deleted segment "${seg.name || id}"`,
      });
      await firestore().collection('marketing_segments').doc(id).delete();
    } catch (e) {
      console.error('segment delete failed', e);
    }
  }, [user]);

  const exportCSV = useCallback((data) => {
    if (!data || !data.length) return;
    const headers = ['Name', 'Description', 'Rules', 'Last updated', 'Owner'];
    const rows = data.map(s => [
      `"${(s.name || '').replace(/"/g, '""')}"`,
      `"${(s.description || '').replace(/"/g, '""')}"`,
      s._ruleCount,
      s._updatedAt ? moment(s._updatedAt).format('YYYY-MM-DD HH:mm') : '',
      s._owner,
    ].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `segments_${moment().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const logsModal = useAuditLogModal();

  return (
    <>
    <ActiveHelper
      title="Marketing Segments"
      selected="segments"
      data={decorated}
      tableColumns={tableColumns}
      customActionButtons={[{
        accessor: 'logs',
        title: 'Logs',
        color: 'info',
        func: (row) => logsModal.open(row),
      }]}
      permenatDelete={true}
      defaultSorting="_updatedAt"
      sortDirection="desc"
      exportCSV={exportCSV}
      customCreateFunction={handleCreate}
      customEditFunction={handleEdit}
      customDeleteFunction={handleDelete}
      confirmCustomDelete={true}
      customEditTitle="Edit"
      customDeleteTitle="Delete"
      showCreateButton={true}
      user={user}
    />
    <AuditLogModal
      isOpen={logsModal.isOpen}
      dismiss={logsModal.close}
      collection="marketing_segments"
      docId={logsModal.row?.id || logsModal.row?.key}
      title={logsModal.row?.name}
    />
    </>
  );
};

export default SegmentsList;
